// src/pages/UserDetail.js
// Which SaaS Applications / Local Applications / Shared Credentials this
// person owns or is assigned to, plus their audit trail across all three.
// Systems has been removed — this used to also have a Systems tab and its
// owner/assignee history was Systems-only; both are now generalized across
// the three remaining types instead.
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { saasApplicationsApi, localApplicationsApi, sharedCredentialsApi } from "../utils/api";
import { auditStatus, formatDue, formatDate, isWebUrl, formatUrl } from "../utils/dates";

const STATUS_BADGE = { ok: "badge-ok", "due-soon": "badge-due", overdue: "badge-overdue", unknown: "badge-neutral" };
const STATUS_LABEL = { ok: "Up to date", "due-soon": "Due soon", overdue: "Overdue", unknown: "Not set" };

const ACTION_LABELS = {
  SYSTEM_CREATED:  { label: "Created",         color: "var(--purple)"   },
  SYSTEM_UPDATED:  { label: "Updated",         color: "var(--gray-500)" },
  SYSTEM_DELETED:  { label: "Deleted",         color: "var(--red)"      },
  USER_ADDED:      { label: "User added",      color: "var(--green)"    },
  USER_UPDATED:    { label: "User updated",    color: "var(--orange)"   },
  USER_REMOVED:    { label: "User removed",    color: "var(--red)"      },
  AUDIT_COMPLETED: { label: "Audit completed", color: "var(--green)"    },
  SYNC_COMPLETED:  { label: "Sync completed",  color: "var(--purple)"   },
};

// The three resource types — each gets its own Owned/Assigned tab, and all
// three feed into the combined Owner/Assignee Audit History tabs.
const TYPE_CONFIGS = [
  { key: "saas",   label: "SaaS Applications",  noun: "SaaS application",  api: saasApplicationsApi,   routeBase: "/saas-applications",
    isOwner: (it, upn) => (it.accessOwners || []).some(o => o.upn?.toLowerCase() === upn) },
  { key: "local",  label: "Local Applications", noun: "local application", api: localApplicationsApi,  routeBase: "/local-applications",
    isOwner: (it, upn) => (it.accessOwners || []).some(o => o.upn?.toLowerCase() === upn) },
  { key: "shared", label: "Shared Credentials", noun: "shared credential", api: sharedCredentialsApi,  routeBase: "/shared-credentials",
    isOwner: (it, upn) => (it.accessOwners || []).some(o => o.upn?.toLowerCase() === upn) },
];

function AuditTable({ entries, emptyMessage }) {
  if (entries.length === 0) {
    return (
      <div className="empty-state" style={{ padding: "32px 0" }}>
        <h3>No history found</h3>
        <p style={{ marginTop: 4 }}>{emptyMessage}</p>
      </div>
    );
  }
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "var(--gray-50)" }}>
          {["When", "Type", "Action", "Item", "Detail"].map(h => (
            <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {entries.map(entry => {
          const meta = ACTION_LABELS[entry.action] || { label: entry.action, color: "var(--gray-500)" };
          return (
            <tr key={`${entry.typeLabel}-${entry.id}`}>
              <td style={{ padding: "10px 16px", fontSize: 12, color: "var(--gray-500)", whiteSpace: "nowrap" }}>
                {formatDate(entry.timestamp)}
                <div style={{ fontSize: 11, color: "var(--gray-400)" }}>
                  {new Date(entry.timestamp).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </td>
              <td style={{ padding: "10px 16px" }}>
                <span className="badge badge-neutral" style={{ fontSize: 11 }}>{entry.typeLabel}</span>
              </td>
              <td style={{ padding: "10px 16px" }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: meta.color }}>{meta.label}</span>
              </td>
              <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 500 }}>
                {entry.itemName || "—"}
              </td>
              <td style={{ padding: "10px 16px", fontSize: 13, color: "var(--gray-500)", maxWidth: 260 }}>
                {entry.detail}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function UrlCell({ url }) {
  if (!url) return <span style={{ color: "var(--gray-400)" }}>—</span>;
  return isWebUrl(url)
    ? <a href={url} target="_blank" rel="noreferrer" style={{ color: "var(--purple)", fontSize: 12 }} onClick={e => e.stopPropagation()}>{formatUrl(url)}</a>
    : <span style={{ fontSize: 12 }}>{url}</span>;
}

// Owned + Assigned tables for one resource type.
function OwnedAssignedTables({ owned, assigned, nounPlural, onView }) {
  return (
    <div>
      <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: "var(--gray-700)" }}>{nounPlural} Owned</h2>
      {owned.length === 0 ? (
        <div className="card empty-state" style={{ marginBottom: 20, padding: "24px" }}>
          <p>Not listed as owner of any registered {nounPlural.toLowerCase()}.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 24 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--gray-50)" }}>
                {["Name", "Category", "URL / Server", "Users", "Status", "Next Audit", ""].map(h => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {owned.map(item => {
                const status = auditStatus(item.nextAuditDue);
                return (
                  <tr key={item.id}>
                    <td style={{ padding: "11px 16px" }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{item.name}</div>
                      {item.description && <div style={{ fontSize: 11, color: "var(--gray-500)" }}>{item.description}</div>}
                    </td>
                    <td style={{ padding: "11px 16px" }}>
                      {item.category ? <span className="badge badge-info" style={{ fontSize: 11 }}>{item.category}</span> : "—"}
                    </td>
                    <td style={{ padding: "11px 16px" }}><UrlCell url={item.url || item.server || item.location} /></td>
                    <td style={{ padding: "11px 16px", fontSize: 13, textAlign: "center" }}><strong>{item.userCount || 0}</strong></td>
                    <td style={{ padding: "11px 16px" }}>
                      <span className={`badge ${STATUS_BADGE[status]}`}>{STATUS_LABEL[status]}</span>
                    </td>
                    <td style={{ padding: "11px 16px", fontSize: 12, color: "var(--gray-500)" }}>{formatDue(item.nextAuditDue)}</td>
                    <td style={{ padding: "11px 12px", whiteSpace: "nowrap" }}>
                      <button className="btn-ghost" style={{ fontSize: 13 }} title="View" onClick={() => onView(item.id)}>👁</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: "var(--gray-700)" }}>{nounPlural} Assigned</h2>
      {assigned.length === 0 ? (
        <div className="card empty-state" style={{ padding: "24px" }}>
          <p>Not assigned as a user on any registered {nounPlural.toLowerCase()}.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--gray-50)" }}>
                {["Name", "Category", "URL / Server", "Access Level"].map(h => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "var(--gray-500)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assigned.map(item => (
                <tr key={item.id} onClick={() => onView(item.id)}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--gray-50)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td style={{ padding: "11px 16px" }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{item.name}</div>
                    {item.description && <div style={{ fontSize: 11, color: "var(--gray-500)" }}>{item.description}</div>}
                  </td>
                  <td style={{ padding: "11px 16px" }}>
                    {item.category ? <span className="badge badge-info" style={{ fontSize: 11 }}>{item.category}</span> : "—"}
                  </td>
                  <td style={{ padding: "11px 16px" }}><UrlCell url={item.url || item.server || item.location} /></td>
                  <td style={{ padding: "11px 16px" }}>
                    {(Array.isArray(item.accessLevel) ? item.accessLevel : [item.accessLevel]).filter(Boolean).map(l => <span key={l} className="badge badge-info" style={{ marginRight: 3 }}>{l}</span>)}
                    {item.accessNotes && <div style={{ fontSize: 11, color: "var(--gray-500)", marginTop: 2 }}>{item.accessNotes}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const EMPTY_TYPE_STATE = { owned: [], assigned: [], loaded: false, loading: false };

export default function UserDetail() {
  const { upn }    = useParams();
  const navigate   = useNavigate();
  const decodedUpn = decodeURIComponent(upn);

  const [person,          setPerson]          = useState(null);
  const [ownerHistory,    setOwnerHistory]    = useState([]);
  const [assigneeHistory, setAssigneeHistory] = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [historyLoading,  setHistoryLoading]  = useState(false);
  const [historyLoaded,   setHistoryLoaded]   = useState(false);
  const [tab,             setTab]             = useState("saas");
  const [exporting,       setExporting]       = useState(false);
  const [exportError,     setExportError]     = useState("");

  // Per-type (SaaS / Local / Shared) owned+assigned, loaded lazily per tab
  const [typeState, setTypeState] = useState({
    saas:   EMPTY_TYPE_STATE,
    local:  EMPTY_TYPE_STATE,
    shared: EMPTY_TYPE_STATE,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const graphRes  = await fetch(`/api/graph/users?q=${encodeURIComponent(decodedUpn)}`);
      const graphData = await graphRes.json();
      const profile   = Array.isArray(graphData)
        ? (graphData.find(u => u.upn?.toLowerCase() === decodedUpn.toLowerCase()) || graphData[0])
        : null;

      // Check if this is a custom user (not in Entra)
      const customRes   = await fetch("/api/custom-users").catch(() => null);
      const customUsers = customRes?.ok ? await customRes.json().catch(() => []) : [];
      const matchCustom = Array.isArray(customUsers)
        ? customUsers.find(u => (u.upn||u.email||"").toLowerCase() === decodedUpn.toLowerCase())
        : null;

      setPerson(profile || matchCustom || { displayName: decodedUpn, email: decodedUpn, upn: decodedUpn, jobTitle: "", department: "" });
    } finally {
      setLoading(false);
    }
  }, [decodedUpn]);

  // Owner/Assignee audit history — aggregated across all three resource
  // types (each has its own audit trail; Systems' history is gone with it).
  const loadHistory = useCallback(async () => {
    if (historyLoaded || historyLoading) return;
    setHistoryLoading(true);
    try {
      const allHistory = [];

      await Promise.allSettled(TYPE_CONFIGS.map(async cfg => {
        try {
          const items = await cfg.api.list();
          await Promise.allSettled(items.map(async item => {
            try {
              const res  = await fetch(`/api/${cfg.routeBase.replace(/^\//, "")}/${item.id}/audits`);
              const data = await res.json();
              if (Array.isArray(data)) {
                allHistory.push(...data.map(e => ({ ...e, itemName: item.name, typeLabel: cfg.label })));
              }
            } catch { /* skip */ }
          }));
        } catch { /* skip */ }
      }));

      allHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // Owner history: actions performed BY this user
      const ownerActions = ["AUDIT_COMPLETED", "SYSTEM_CREATED", "SYSTEM_UPDATED", "USER_ADDED", "USER_UPDATED", "USER_REMOVED"];
      setOwnerHistory(allHistory.filter(e =>
        ownerActions.includes(e.action) &&
        e.auditedByUpn?.toLowerCase() === decodedUpn.toLowerCase()
      ));

      // Assignee history: USER_* actions where this user's UPN or email appears in the detail
      const upnLower = decodedUpn.toLowerCase();
      setAssigneeHistory(allHistory.filter(e =>
        ["USER_ADDED", "USER_UPDATED", "USER_REMOVED"].includes(e.action) &&
        e.detail?.toLowerCase().includes(upnLower)
      ));

      setHistoryLoaded(true);
    } finally {
      setHistoryLoading(false);
    }
  }, [decodedUpn, historyLoaded, historyLoading]);

  // Load owned/assigned for one of the three types, the first time its tab is opened.
  const loadType = useCallback(async (key) => {
    const cfg = TYPE_CONFIGS.find(t => t.key === key);
    if (!cfg) return { owned: [], assigned: [] };
    setTypeState(s => ({ ...s, [key]: { ...s[key], loading: true } }));
    try {
      const items = await cfg.api.list();
      const ownedItems = [];
      const assignedItems = [];
      const upnLower = decodedUpn.toLowerCase();
      for (const it of items) {
        if (cfg.isOwner(it, upnLower)) {
          ownedItems.push({ ...it });
        }
        try {
          const data = await cfg.api.get(it.id);
          const match = (data.users || []).find(u => u.active && u.upn?.toLowerCase() === decodedUpn.toLowerCase());
          if (match) assignedItems.push({ ...it, accessLevel: match.accessLevel, accessNotes: match.notes });
        } catch { /* skip */ }
      }
      setTypeState(s => ({ ...s, [key]: { owned: ownedItems, assigned: assignedItems, loaded: true, loading: false } }));
      return { owned: ownedItems, assigned: assignedItems };
    } catch {
      setTypeState(s => ({ ...s, [key]: { owned: [], assigned: [], loaded: true, loading: false } }));
      return { owned: [], assigned: [] };
    }
  }, [decodedUpn]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab === "ownerHistory" || tab === "assigneeHistory") loadHistory();
    const cfg = TYPE_CONFIGS.find(t => t.key === tab);
    if (cfg && !typeState[cfg.key].loaded && !typeState[cfg.key].loading) loadType(cfg.key);
  }, [tab]); // run when the active tab changes; loaders below guard against re-fetching

  const handleExportPdf = async () => {
    setExporting(true); setExportError("");
    try {
      // Use whatever's already loaded for a type; fetch fresh for anything
      // the admin hasn't clicked into yet — loadType returns the data
      // directly rather than relying on React state catching up in time.
      const dataByType = {};
      for (const cfg of TYPE_CONFIGS) {
        const s = typeState[cfg.key];
        dataByType[cfg.key] = s.loaded ? { owned: s.owned, assigned: s.assigned } : await loadType(cfg.key);
      }

      const { default: jsPDF } = await import("jspdf");
      const autoTableModule = await import("jspdf-autotable");
      const autoTable = autoTableModule.default;

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const PURPLE = [83, 74, 183];
      const LIGHT  = [238, 237, 254];
      const personLabel = person?.displayName || decodedUpn;

      const drawHeading = (text, y) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(...PURPLE);
        doc.text(text, 14, y);
      };

      TYPE_CONFIGS.forEach((cfg, i) => {
        if (i > 0) doc.addPage();
        const { owned, assigned } = dataByType[cfg.key];

        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.setTextColor(...PURPLE);
        doc.text(`${personLabel} — ${cfg.label}`, 14, 16);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(140, 140, 140);
        doc.text(`Generated ${new Date().toLocaleDateString("en-GB")}`, doc.internal.pageSize.width - 14, 16, { align: "right" });

        drawHeading(`Owned (${owned.length})`, 26);
        if (owned.length === 0) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(140, 140, 140);
          doc.text(`No ${cfg.label.toLowerCase()} owned.`, 14, 33);
          doc.lastAutoTable = { finalY: 33 };
        } else {
          autoTable(doc, {
            head: [["Name", "Category", "Co-owner(s)", "Status", "Next Audit Due"]],
            body: owned.map(s => [
              s.name || "—", s.category || "—",
              (s.accessOwners || []).filter(o => o.upn?.toLowerCase() !== decodedUpn.toLowerCase()).map(o => o.displayName).join(", ") || "—",
              (STATUS_LABEL[auditStatus(s.nextAuditDue)] || "—"),
              formatDue(s.nextAuditDue),
            ]),
            startY: 30,
            margin: { left: 14, right: 14 },
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { fillColor: PURPLE, textColor: 255, fontStyle: "bold", fontSize: 9.5 },
            alternateRowStyles: { fillColor: LIGHT },
          });
        }

        const assignedY = doc.lastAutoTable.finalY + 14;
        drawHeading(`Assigned (${assigned.length})`, assignedY);
        if (assigned.length === 0) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(140, 140, 140);
          doc.text(`Not assigned as a user on any ${cfg.label.toLowerCase()}.`, 14, assignedY + 7);
        } else {
          autoTable(doc, {
            head: [["Name", "Category", "Role", "System Owner(s)"]],
            body: assigned.map(s => [
              s.name || "—", s.category || "—",
              (Array.isArray(s.accessLevel) ? s.accessLevel.join(", ") : s.accessLevel) || "—",
              (s.accessOwners || []).map(o => o.displayName).join(", ") || "—",
            ]),
            startY: assignedY + 4,
            margin: { left: 14, right: 14 },
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { fillColor: PURPLE, textColor: 255, fontStyle: "bold", fontSize: 9.5 },
            alternateRowStyles: { fillColor: LIGHT },
          });
        }

        doc.setFontSize(7.5);
        doc.setTextColor(180, 180, 180);
        doc.text(`SUFC System Governance Portal — ${personLabel} — Page ${doc.internal.getNumberOfPages()}`, 14, doc.internal.pageSize.height - 6);
      });

      const safeNamePart = personLabel.replace(/[^a-z0-9]/gi, "_");
      doc.save(`SUFC_${safeNamePart}_Access_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      setExportError("Failed to generate PDF: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80 }}>
      <span className="spinner" />
    </div>
  );

  const initials = (person?.displayName || "U").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div>
      <button className="btn-ghost" style={{ marginBottom: 16, paddingLeft: 0, color: "var(--purple)" }} onClick={() => navigate("/users")}>
        ← Back to Users
      </button>

      {/* Profile header */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--purple)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 600, flexShrink: 0 }}>
            {initials}
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 19, fontWeight: 600, marginBottom: 4 }}>{person?.displayName}</h1>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 13, color: "var(--gray-500)" }}>
              {person?.jobTitle   && <span>💼 {person.jobTitle}</span>}
              {person?.department && <span>🏢 {person.department}</span>}
              {person?.email      && <span>✉️ {person.email}</span>}
            </div>
          </div>
          <button type="button" className="btn-secondary" style={{ fontSize: 13, whiteSpace: "nowrap" }} disabled={exporting} onClick={handleExportPdf}>
            {exporting ? <><span className="spinner" style={{ width: 12, height: 12 }} /> Preparing…</> : "⬇ Export PDF"}
          </button>
        </div>
        {exportError && <div style={{ fontSize: 12, color: "var(--red)", marginTop: 10 }}>{exportError}</div>}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", marginBottom: 20, flexWrap: "wrap" }}>
        {[
          ...TYPE_CONFIGS.map(cfg => ({
            key: cfg.key,
            label: typeState[cfg.key].loaded
              ? `${cfg.label} (${typeState[cfg.key].owned.length + typeState[cfg.key].assigned.length})`
              : cfg.label,
          })),
          { key: "ownerHistory",    label: "Owner Audit History"    },
          { key: "assigneeHistory", label: "Assignee Audit History" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: "9px 18px", border: "none", borderRadius: 0, background: "transparent", cursor: "pointer",
              fontWeight: 500, fontSize: 13, whiteSpace: "nowrap",
              color:        tab === t.key ? "var(--purple)" : "var(--gray-500)",
              borderBottom: tab === t.key ? "2px solid var(--purple)" : "2px solid transparent",
              marginBottom: -1 }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* SaaS Applications / Local Applications / Shared Credentials tabs */}
      {TYPE_CONFIGS.map(cfg => {
        if (tab !== cfg.key) return null;
        const s = typeState[cfg.key];
        if (s.loading || !s.loaded) {
          return (
            <div key={cfg.key} style={{ textAlign: "center", padding: 48 }}><span className="spinner" /></div>
          );
        }
        return (
          <OwnedAssignedTables
            key={cfg.key}
            owned={s.owned}
            assigned={s.assigned}
            nounPlural={cfg.label}
            onView={(id) => navigate(`${cfg.routeBase}/${id}`)}
          />
        );
      })}

      {/* Owner Audit History */}
      {tab === "ownerHistory" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {historyLoading
            ? <div style={{ textAlign: "center", padding: 40 }}><span className="spinner" /></div>
            : <AuditTable entries={ownerHistory} emptyMessage="No audit actions found where this person acted as an owner or admin, across any type." />
          }
        </div>
      )}

      {/* Assignee Audit History */}
      {tab === "assigneeHistory" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {historyLoading
            ? <div style={{ textAlign: "center", padding: 40 }}><span className="spinner" /></div>
            : <AuditTable entries={assigneeHistory} emptyMessage="No audit actions found that reference this person, across any type. History is recorded when they are added, removed, or have access changed." />
          }
        </div>
      )}
    </div>
  );
}
